import React from 'react';
import { useEditor } from '../../context/EditorContext';
import { RibbonGroup, RibbonSeparator, SmallButton, Stack, Row, SwatchButton, RibbonNumber, SizeCombo } from './RibbonPrimitives';
import { ColorPicker } from '../editor/ColorPicker';
import { FontPicker } from '../editor/FontPicker';
import { FONT_SIZE_LIST } from '../../hooks/useTextCommands';
import { pxToPt, ptToPx } from '../../lib/units';
import { useFillExtras, useOutlineExtras } from './FillMenus';
import { fillSwatchCss } from '../../lib/colors';
import { strokeHasGradient, strokeSwatchCss, tableBorderStroke, tableBorderProps, solidStroke } from '../../lib/fills';
import { Rows3, Columns3, Trash2, PaintBucket, PenLine, Baseline, Table as TableIcon, LayoutList } from 'lucide-react';

export const TableDesignTab = () => {
  const { dispatch, rawDispatch, getSelectedObjects, openDialog } = useEditor();
  const selected = getSelectedObjects().filter((o) => o.type === 'table');
  const one = selected[0];
  const ids = selected.map((o) => o.id);
  const p = one?.properties || {};
  const set = (props) => ids.length && dispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids, props } });
  const fillExtras = useFillExtras({ current: p.fill, apply: (fill) => set({ fill }), target: 'fill', testPrefix: 'table-fill' });
  // Borders: the outline menu's Gradient ▸ row (variations, presets, shimmer, gradient creator) through the table adapters.
  const borderStroke = tableBorderStroke(p);
  const borderExtras = useOutlineExtras({ stroke: borderStroke, apply: (stroke) => set(tableBorderProps(stroke)), testPrefix: 'table-border' }).filter((i) => i.id === 'gradient');
  const previewFont = (family) => rawDispatch({ type: 'SET_FONT_PREVIEW', payload: family && ids.length ? { ids, family } : null });

  const resize = (dr, dc) => {
    if (!one) return;
    const rows = Math.max(1, (p.rows || 1) + dr), cols = Math.max(1, (p.cols || 1) + dc);
    const cells = Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (__, c) => (p.cells?.[r]?.[c] ? { ...p.cells[r][c] } : { text: '' })));
    dispatch({ type: 'UPDATE_OBJECT', payload: { id: one.id, data: { properties: { ...p, rows, cols, cells }, height: dr !== 0 ? Math.max(30, one.height + dr * (one.height / Math.max(1, p.rows || 1))) : one.height } } });
  };

  return (
    <>
      <RibbonGroup label="Table Style Options" testId="ribbon-group-table-options">
        <Stack>
          <SmallButton icon={LayoutList} label="Header Row" active={!!p.headerRow} onClick={() => set({ headerRow: !p.headerRow })} testId="ribbon-command-table-header-row" />
          <SmallButton icon={Rows3} label="Banded Rows" active={!!p.banded} onClick={() => set({ banded: !p.banded })} testId="ribbon-command-table-banded" />
        </Stack>
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup label="Rows & Columns" testId="ribbon-group-table-rows-cols">
        <Stack>
          <SmallButton icon={Rows3} label="Insert Row" onClick={() => resize(1, 0)} disabled={!one} testId="ribbon-command-table-add-row" />
          <SmallButton icon={Rows3} label="Delete Row" onClick={() => resize(-1, 0)} disabled={!one || (p.rows || 1) <= 1} testId="ribbon-command-table-remove-row" />
        </Stack>
        <Stack>
          <SmallButton icon={Columns3} label="Insert Column" onClick={() => resize(0, 1)} disabled={!one} testId="ribbon-command-table-add-col" />
          <SmallButton icon={Columns3} label="Delete Column" onClick={() => resize(0, -1)} disabled={!one || (p.cols || 1) <= 1} testId="ribbon-command-table-remove-col" />
        </Stack>
        <Stack>
          <Row className="gap-1"><span className="text-[10px] text-muted-foreground w-10">Padding</span><RibbonNumber value={p.cellPadding ?? 4} onChange={(v) => set({ cellPadding: v })} min={0} max={40} width="w-12" testId="ribbon-table-padding" title="Cell padding" /></Row>
          <SmallButton icon={Trash2} label="Delete Table" onClick={() => dispatch({ type: 'DELETE_OBJECTS', payload: ids })} disabled={!ids.length} testId="ribbon-command-table-delete" />
        </Stack>
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup label="Table Formats" testId="ribbon-group-table-formats">
        <Stack>
          <ColorPicker value={p.fill?.type === 'solid' ? p.fill.color : null} onChange={(hex) => set({ fill: hex ? { type: 'solid', color: hex } : { type: 'none' } })} label="Fill" onFillEffects={() => openDialog('fill-effects', { target: 'fill' })} testId="table-fill-picker" extras={fillExtras}>
            <SwatchButton icon={PaintBucket} color={fillSwatchCss(p.fill)} tip="Table Fill" label="Fill" testId="ribbon-command-table-fill" />
          </ColorPicker>
          <ColorPicker value={p.headerFill || null} onChange={(hex) => set({ headerFill: hex || null })} label="Header Fill" testId="table-header-fill-picker">
            <SwatchButton icon={TableIcon} color={p.headerFill || null} tip="Header Row Fill" label="Header Fill" testId="ribbon-command-table-header-fill" />
          </ColorPicker>
        </Stack>
        <Stack>
          <ColorPicker value={strokeHasGradient(borderStroke) ? null : p.borderColor || '#000000'} onChange={(hex) => set(hex ? tableBorderProps(solidStroke(borderStroke, hex, p.borderWidth || 1)) : { borderWidth: 0 })} label="Borders" testId="table-border-picker" extras={borderExtras}>
            <SwatchButton icon={PenLine} color={strokeSwatchCss(borderStroke) || p.borderColor || '#000000'} tip="Border Color" label="Borders" testId="ribbon-command-table-border" />
          </ColorPicker>
          <Row className="gap-1"><span className="text-[10px] text-muted-foreground w-10">Weight</span><RibbonNumber value={p.borderWidth ?? 1} onChange={(v) => set({ borderWidth: v })} min={0} max={12} step={0.5} width="w-12" testId="ribbon-table-border-width" title="Border weight" /></Row>
        </Stack>
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup label="Text" testId="ribbon-group-table-text">
        <Stack className="gap-[3px]">
          <Row className="gap-1">
            <FontPicker compact value={p.fontFamily || 'Inter'} onChange={(f) => set({ fontFamily: f })} onPreview={previewFont} testId="ribbon-table-font" />
            <SizeCombo value={pxToPt(p.fontSize || 14)} onChange={(pt) => set({ fontSize: ptToPx(pt) })} options={FONT_SIZE_LIST} testId="ribbon-table-font-size" title="Font Size (pt)" />
          </Row>
          <ColorPicker value={p.color || '#000000'} onChange={(hex) => set({ color: hex || '#000000' })} allowNoFill={false} label="Text Color" testId="table-text-color-picker">
            <SwatchButton icon={Baseline} color={p.color || '#000000'} tip="Text Color" label="Text Color" testId="ribbon-command-table-text-color" />
          </ColorPicker>
        </Stack>
      </RibbonGroup>
    </>
  );
};
